import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Modal,
  TextField,
} from "@mui/material";
import { InputOptionComponent } from "components";
import InputDatePickerComponent from "components/input-date-picker/InputDatePickerComponent";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGetCompanyCategories,
  useGetCompanyDetails,
  useGetCompanyIndustries,
} from "services/company";
import {
  useCreateClient,
  useEditClient,
} from "../service/ClientManagementService";
import { formatNumberOnly, formatPhoneNumberPayloadBE } from "services/util";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import { AddOutlined } from "@mui/icons-material";

const featureOptions = [
  { label: "Free", value: "FREE" },
  { label: "Pro", value: "PRO" },
];

const statusOptions = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

const CreateEditClientModal = ({ modalProps = {}, id, onClose }) => {
  const { setMessage } = useSnackbar();

  const [formData, setFormData] = useState({});
  const [emailRecipients, setEmailRecipients] = useState([undefined]);

  const { response: companyDetail, loading: loadingDetail } =
    useGetCompanyDetails({ id });
  const { response: industriesResp } = useGetCompanyIndustries();
  const { response: categoriesResp } = useGetCompanyCategories();
  const { mutate: createClient, loading: loadingCreate } = useCreateClient();
  const { mutate: updateClient, loading: loadingUpdate } = useEditClient({
    id,
  });

  const isEdit = useMemo(() => Boolean(id), [id]);

  const industryOptions = useMemo(
    () =>
      industriesResp?.data?.map((el) => ({
        label: el?.name,
        value: Number(el?.id),
      })),
    [industriesResp],
  );

  const categoryOptions = useMemo(
    () =>
      categoriesResp?.data?.map((el) => ({
        label: el?.name,
        value: Number(el?.id),
      })),
    [categoriesResp],
  );

  const handleSubmitForm = useCallback(async () => {
    if (!formData?.name) {
      setMessage({
        desc: "Name cannot be blank",
        type: "error",
      });
      return;
    }

    if (!formData?.effect_code) {
      setMessage({
        desc: "Code cannot be blank",
        type: "error",
      });
      return;
    }

    if (!id) {
      if (!formData?.admin_phone_number) {
        setMessage({
          desc: "Admin Phone Number cannot be blank",
          type: "error",
        });
        return;
      }

      const modifiedFormData = {
        ...formData,
        email_recipients: formData?.email_recipients?.filter((el) =>
          Boolean(el),
        ),
        admin_phone_number: formatPhoneNumberPayloadBE(
          formData?.admin_phone_number,
        ),
        year_established: Number(
          formData?.year_established || dayjs(Date.now())?.format("YYYY"),
        ),
      };
      const response = await createClient(modifiedFormData);
      if (
        response?.info?.description === "Company is created successfully" ||
        response?.info?.title === "success"
      ) {
        setMessage({
          desc: "Successfully created new client.",
          type: "success",
        });
        onClose();
      } else {
        const errMessage =
          response ?? "Failed to create client. Please try again.";
        setMessage({
          desc: errMessage,
          type: "error",
        });
      }
    } else {
      const modifiedFormData = {
        ...formData,
        email_recipients: formData?.email_recipients?.filter((el) =>
          Boolean(el),
        ),
      };
      const response = await updateClient(modifiedFormData);
      if (
        response?.info?.description === "Company is updated successfully" ||
        response?.info?.title === "success"
      ) {
        setMessage({
          desc: "Successfully updated client.",
          type: "success",
        });
        onClose();
      } else {
        const errMessage =
          response ?? "Failed to update client. Please try again.";
        setMessage({
          desc: errMessage,
          type: "error",
        });
      }
    }
  }, [id, formData, createClient, setMessage, onClose, updateClient]);

  const handleOnChangeFormInput = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddEmailRecipient = () => {
    setEmailRecipients((prev) => [...prev, undefined]);
    setFormData((prev) => ({
      ...prev,
      email_recipients: [
        ...(prev?.email_recipients?.length
          ? prev?.email_recipients
          : [undefined]),
        undefined,
      ],
    }));
  };

  const dynamicForm = useMemo(() => {
    return emailRecipients?.map((_el, idx) => (
      <div
        className='flex felx-row gap-3 items-center'
        key={`email-recipients-${idx}`}
      >
        <p className='text-[#5F737F] text-xs'>{`${idx < 9 ? "0" : ""}${
          idx + 1
        }`}</p>
        <TextField
          className='w-[486px]'
          placeholder='Insert e-mail address'
          value={formData?.["email_recipients"]?.[idx]}
          onChange={({ target: { value } } = {}) => {
            const newEmailRecipients = [
              ...(formData?.["email_recipients"] || []),
            ];
            newEmailRecipients[idx] = value;
            handleOnChangeFormInput("email_recipients", newEmailRecipients);
          }}
        />
        {idx < 9 && idx === emailRecipients?.length - 1 && (
          <div
            className='flex flex-row gap-2 cursor-pointer items-center'
            onClick={handleAddEmailRecipient}
          >
            <AddOutlined />
            <p className='text-[#5F737F] text-xs'>Add new email</p>
          </div>
        )}
      </div>
    ));
  }, [emailRecipients, formData]);

  // auto fill form when edit modal is opened
  useEffect(() => {
    if (id && companyDetail) {
      const clientDetail = companyDetail?.data || {};
      setFormData({
        name: clientDetail?.name,
        effect_code: clientDetail?.effect_code,
        subscription_type: clientDetail?.subscription_type,
        email_recipients: clientDetail?.email_recipients,
        year_established: Number(
          clientDetail?.year_established || dayjs(Date.now())?.format("YYYY"),
        ),
        industry: industryOptions?.find(
          (el) => el?.label === clientDetail?.industry_name,
        )?.value,
        category: categoryOptions?.find(
          (el) => el?.label === clientDetail?.category_name,
        )?.value,
        status: clientDetail?.status,
      });

      if (clientDetail?.email_recipients?.length) {
        setEmailRecipients(clientDetail?.email_recipients);
      }
    }
  }, [id, loadingDetail, companyDetail, industryOptions, categoryOptions]);

  return (
    <Modal {...modalProps}>
      <Box
        sx={{
          position: "absolute",
          top: "55%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          overflow: "scroll",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          px: "20px",
          py: "25px",
        }}
      >
        <div className='create-edit-modal-content max-h-[650px] overflow-scroll'>
          <div className='font-semibold mb-12'>
            {`${isEdit ? "Edit" : "Add New"} Client`}
          </div>
          <div className='font-semibold text-sm mb-5'>Client Information</div>
          <div
            className={`grid gap-4 pb-8 ${
              !isEdit ? "border-b border-[#CDD4D7]" : ""
            }`}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <div className='form-input'>
                  <p className='form-input-label'>Client name</p>
                  <TextField
                    placeholder='Insert client name'
                    value={formData?.name}
                    onChange={({ target: { value } } = {}) =>
                      handleOnChangeFormInput("name", value)
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={6}>
                <div className='form-input'>
                  <p className='form-input-label'>Client Code</p>
                  <TextField
                    placeholder='Insert client code'
                    value={formData?.effect_code}
                    onChange={({ target: { value } } = {}) =>
                      handleOnChangeFormInput(
                        "effect_code",
                        value?.toUpperCase(),
                      )
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={6}>
                <div className='form-input'>
                  <p className='form-input-label'>Industry</p>
                  <InputOptionComponent
                    width='full'
                    label='Select client industry'
                    options={industryOptions ?? []}
                    value={industryOptions?.find(
                      (el) => el?.value === formData?.industry,
                    )}
                    onSelect={(opt) =>
                      handleOnChangeFormInput("industry", opt?.value)
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={6}>
                <div className='form-input'>
                  <p className='form-input-label'>Listing Year</p>
                  <InputDatePickerComponent
                    views={["year"]}
                    maxDate={dayjs()}
                    value={
                      formData?.year_established
                        ? dayjs(String(formData?.year_established))
                        : dayjs()
                    }
                    onChange={(date) =>
                      handleOnChangeFormInput(
                        "year_established",
                        Number(dayjs(date).format("YYYY")),
                      )
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={6}>
                <div className='form-input'>
                  <p className='form-input-label'>Category</p>
                  <InputOptionComponent
                    width='full'
                    label='Select category'
                    options={categoryOptions ?? []}
                    value={categoryOptions?.find(
                      (el) => el?.value === formData?.category,
                    )}
                    onSelect={(opt) =>
                      handleOnChangeFormInput("category", opt?.value)
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={6}>
                <div className='form-input'>
                  <p className='form-input-label'>Feature</p>
                  <InputOptionComponent
                    width='full'
                    label='Select plan'
                    options={featureOptions}
                    value={featureOptions?.find(
                      (el) => el?.value === formData?.subscription_type,
                    )}
                    onSelect={(opt) =>
                      handleOnChangeFormInput("subscription_type", opt?.value)
                    }
                  />
                </div>
              </Grid>
              {isEdit && (
                <Grid item xs={6}>
                  <div className='form-input'>
                    <p className='form-input-label'>Status</p>
                    <InputOptionComponent
                      width='full'
                      label='Select status'
                      options={statusOptions}
                      value={statusOptions?.find(
                        (el) =>
                          el?.value === (isEdit ? formData?.status : "ACTIVE"),
                      )}
                      onSelect={(opt) =>
                        handleOnChangeFormInput("status", opt?.value)
                      }
                    />
                  </div>
                </Grid>
              )}
            </Grid>
          </div>

          {!isEdit && (
            <div className='mb-8'>
              <div className='mt-8 mb-5 font-semibold text-sm'>
                Admin Information
              </div>
              <div className='grid gap-4 mb-5'>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <div className='form-input'>
                      <p className='form-input-label'>Name</p>
                      <TextField
                        placeholder='Insert admin name'
                        value={formData?.admin_name}
                        onChange={({ target: { value } } = {}) =>
                          handleOnChangeFormInput("admin_name", value)
                        }
                      />
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className='form-input'>
                      <p className='form-input-label'>E-mail address</p>
                      <TextField
                        placeholder='Insert e-mail address'
                        value={formData?.admin_email}
                        onChange={({ target: { value } } = {}) =>
                          handleOnChangeFormInput("admin_email", value)
                        }
                      />
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className='form-input'>
                      <p className='form-input-label'>Phone Number</p>
                      <TextField
                        InputProps={{
                          startAdornment: (
                            <InputAdornment
                              sx={{
                                backgroundColor: (theme) =>
                                  theme.palette.divider,
                                padding: "20px 14px",
                                borderRight: "1px solid #727273",
                                borderTopLeftRadius: "10px",
                                borderBottomLeftRadius: "10px",
                              }}
                              position='start'
                            >
                              <p>+62</p>
                            </InputAdornment>
                          ),
                        }}
                        placeholder='8...'
                        value={formData?.admin_phone_number}
                        onChange={({ target: { value } } = {}) =>
                          handleOnChangeFormInput(
                            "admin_phone_number",
                            formatNumberOnly(value),
                          )
                        }
                      />
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className='form-input'>
                      <p className='form-input-label'>Role</p>
                      <TextField disabled value='Admin' />
                    </div>
                  </Grid>
                </Grid>
              </div>
            </div>
          )}

          <div className='font-semibold text-sm mb-5'>
            Monthly Report Recipient
          </div>
          <div className='form-input mb-8'>
            <p className='form-input-label'>Email address</p>
            {dynamicForm}
          </div>

          <div className='flex gap-4 justify-end'>
            <Button
              className='!bg-[#556FB5] w-[200px] py-2 px-[17px]'
              onClick={handleSubmitForm}
              variant='contained'
              disabled={loadingCreate || loadingUpdate}
            >
              {`${isEdit ? "Edit" : "Create"} Client`}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default CreateEditClientModal;
